/**
 * reportPlanLanes — the lane an analysis sits in for a user-facing picker, and
 * the one-line reason shown when it is not runnable yet.
 *
 * Lifted verbatim from `GenerateStage.tsx` (P2.1, 2026-09-05) so the New
 * Report wizard's type-select step and the Path-to-GO Generate stage read the
 * SAME predicate and the SAME strings: a report type is placed in "Available
 * later" by exactly one function, and the reason a user sees before choosing
 * a type is the reason the Generate stage shows beside it. No new predicate —
 * the server chokepoint (`assertAnalysisPrerequisitesReady`) stays the only
 * admission test; these are advisory labels over the plan it already agrees
 * with.
 */
import type { ReportPlanItem } from './reportPlan';

export type AnalysisLane = NonNullable<ReportPlanItem['eligibility']>['group'];

export function analysisLane(item: ReportPlanItem): AnalysisLane {
  if (item.eligibility) return item.eligibility.group;
  if (item.role === 'required') return 'run-now';
  if (
    item.lifecycle === 'needs-input'
    && (item.type === 'financial' || item.type === 'supply-chain-risk')
  ) return 'available-later';
  return 'optional';
}

export function laterLabel(item: ReportPlanItem): string {
  // Two-stage lock outranks the per-type input hints: the model or BOM may be
  // complete and the analysis still waits for a promoted scenario.
  if (item.eligibility?.reasonCode === 'stage-locked') return item.eligibility.reason;
  if (item.type === 'financial') return 'Complete the financial model first';
  if (item.type === 'supply-chain-risk') return 'Choose equipment first';
  return item.eligibility?.reason ?? 'Prepare its source inputs first';
}
